import React, { useEffect, useState, useRef } from "react";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import ZoomModal from "./zoomModal";
import { divisionLevelHeatMap } from "../../services/api-services";
import { toastEmitter } from "../../utills/utilities";
import { API_RESPONSE } from "../../utills/app-constant";
import Modal from "../../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../../utills/image-constant";
import Remark from "../../admin/components/Charts/Remark";

const JobHeatMap = ({ selectedCompanyId, selectedCampaignId, type, graphName, title = "Job Level Heatmap" }) => {
  const chartRef = useRef(null);
  const [jobHeatmapData, setJobHeatmapData] = useState([]);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState(0);
  const [colorRanges, setColorRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);
  const modalId = `zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const getHeatmapData = async () => {
    setLoading(true);
    try {
      const response = await divisionLevelHeatMap(selectedCompanyId, selectedCampaignId, "hierarchy", type, graphName);
      if (response?.data?.responseCode === 200) {
        const data = response.data;
        setRemarkText(data.remark || "");
        setRemarkId(data.remarkId || null);
        setIsAttempt(data.isAttempt ?? 0);
        setJobHeatmapData(data.responseBody || []);
        setColorRanges(data.colorList || []);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHeatmapData();
  }, [selectedCompanyId, selectedCampaignId, type]);

  // Generate chart data
  const categories = [];
  let data = [];

  if (jobHeatmapData?.length > 0) {
    jobHeatmapData.forEach((item) => {
      item.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
        if (!categories.includes(dimension.dimensionName)) categories.push(dimension.dimensionName);
        const entry = data.find((d) => d.name === item.name);
        if (entry) {
          entry.values.push(parseFloat(dimension.percentage?.toFixed(2)));
        } else {
          data.push({
            name: item.name,
            values: [parseFloat(dimension.percentage?.toFixed(2))],
          });
        }
      });
    });
  }

  const grandTotal = categories.map((_, idx) => {
    const sum = data.reduce((total, row) => total + (row.values[idx] || 0), 0);
    return parseFloat((sum / data.length).toFixed(2));
  });

  const series = [
    ...data.map((row) => ({ name: row.name, data: row.values })),
    { name: "Grand Total (Average)", data: grandTotal },
  ];

  const colorRangesArray = colorRanges.map((range) => ({
    from: range.rangeFrom,
    to: range.rangeTo,
    color: range.colorName,
    name: `${range.rangeFrom}% - ${range.rangeTo}%`,
  }));

  const options = {
    chart: {
      id: "job_level_heatmap",
      type: "heatmap",
      toolbar: { show: false },
    },
    plotOptions: {
      heatmap: {
        colorScale: { ranges: colorRangesArray },
      },
    },
    xaxis: {
      categories: categories,
      labels: { style: { fontSize: "12px", fontWeight: "bold" } },
      position: "top",
    },
    yaxis: {
      labels: {
        style: { fontSize: "12px", fontWeight: "bold" },
        formatter: (value) =>
          value === "Grand Total (Average)" ? "Grand Total" : value,
      },
      reversed: true,
    },
    dataLabels: {
      enabled: true,
      formatter: (value) => `${value}%`,
      style: { fontSize: "10px", fontWeight: "bold", colors: ["#000"] },
    },
    tooltip: {
      enabled: true,
      y: { formatter: (value) => `${value}%` },
    },
    legend: {
      show: true,
      position: "top",
      markers: { width: 12, height: 12 },
      itemMargin: { horizontal: 10 },
    },
  };

  // Export
  const exportAsExcel = () => {
    const headers = ["Dimension Name", "Count"];
    const rows = jobHeatmapData.map(d => [d.dimensionName, d.count]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${title}.xlsx`);
  };

  const exportAsPDF = async () => {
    const element = document.getElementById(`chart-box-${title}`);
    if (!element) return;
    const canvas = await html2canvas(element);
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 10, 10, 270, 140);
    pdf.save(`${title}.pdf`);
  };

  // Image export
  const exportAsImage = async () => {
    const chartEl = chartRef.current;
    if (!chartEl) return;
    const canvas = await html2canvas(chartEl);
    canvas.toBlob((blob) => saveAs(blob, "JobLevelHeatmap.png"));
  };

  return (
    <div id="chart-heatmap-container" className="card h-100">
      {/* Toolbar */}
      <div className="d-flex justify-content-between align-items-center overall-text mb-3 p-2">
        <h5>{title}</h5>
        <div className="d-flex align-items-center">
          <button
            className="border-0 bg-transparent-btn me-2"
            data-bs-toggle="modal"
            data-bs-target={`#${modalId}`}
          >
            <img src={ZOOM_ICON} alt="zoom" />
          </button>
          <div className="dropdown">
            <button
              className="bg-transparent icon-btn"
              type="button"
              data-bs-toggle="dropdown"
            >
              <img src={MENU_ICON} alt="menu" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button className="dropdown-item" onClick={exportAsExcel}>
                  Download Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportAsPDF}>
                  Export PDF
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportAsImage}>
                  Download Image
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loader-container"><div className="loader"></div></div>
      ) : jobHeatmapData.length > 0 ? (
        <div ref={chartRef}>
          <Chart
            options={options}
            series={series}
            type="heatmap"
            height={350}
            width="100%"
          />
        </div>
      ) : (
        <p className="text-center my-4">No data available to display.</p>
      )}

      {/* Comment Section */}
      <div className="d-flex justify-content-end ">
        <button
          title="Add Comment"
          onClick={() => {
            setShowRemarkModal(true);
            setTimeout(() => {
              if (modalRef.current) modalRef.current.click();
            }, 0);
          }}
          className="border-0 bg-transparent position-absolute"
          style={{
            bottom: "10px",
            right: "10px",
          }}
        >
          <img src={CHAT_ICON} alt="chat" />
        </button>
      </div>
      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          <Remark
            selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remarkText}
            remarkId={remarkId}
            isAttempt={0}
            setRemarkprops={setRemarkText}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}


      {/* Zoom Modal */}
      <ZoomModal
        id={modalId}
        title={title}
        chartOptions={options}
        seriesData={series}
        type="heatmap"
        height={300}
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />
    </div>
  );
};

export default JobHeatMap;
